import * as React from 'react';
import { cn } from '@noor/lib/cn';

export interface ChatMessageActionProps extends React.ComponentProps<'button'> {
  /** Accessible name — these are icon-only controls, so it is required. */
  label: string;
  /** Tints the control as destructive (delete). */
  destructive?: boolean;
  children: React.ReactNode;
}

/**
 * One control in a `Chat.Bubble`'s hover action row (reply / forward / edit /
 * delete). Sits below the balloon next to the timestamp, so it is deliberately
 * small and chromeless — a bare glyph that tints on hover rather than a chip
 * with its own surface, which at this size would out-shout the message.
 *
 * `type="button"` by default so dropping one inside a form (e.g. an inline
 * message editor) can't accidentally submit it.
 */
function ChatMessageAction({
  className,
  label,
  destructive = false,
  children,
  ...props
}: ChatMessageActionProps) {
  return (
    <button
      type="button"
      data-slot="chat-message-action"
      aria-label={label}
      title={label}
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded',
        'text-muted-foreground',
        'transition-[color,background-color] duration-150 ease-out',
        'outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
        'disabled:pointer-events-none disabled:opacity-50',
        destructive
          ? 'hover:bg-destructive/10 hover:text-destructive'
          : 'hover:bg-muted hover:text-foreground',
        '[&_svg]:size-3.5 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export { ChatMessageAction };
